import React from "react";
import { AppConfigContext } from "./AppConfigContext";
import { SSEModel } from "../api/sse/SSEModel";
import { SSEAdapter } from "../api/sse/SSEAdapter";
import { SSEEvent } from "../api/sse/SSE.domain";

interface SSEContextProps {
  subscribe: (eventType: string, callback: (data: SSEEvent) => void, topic?: string) => void;
  unsubscribe: (eventType: string, callback: (data: SSEEvent) => void) => void;
  isConnected: boolean;
}

export const SSEContext = React.createContext<SSEContextProps | undefined>(undefined);

interface SSEProviderProps {
  children: React.ReactNode;
}

export function SSEProvider({ children }: SSEProviderProps) {
  const appConfig = React.useContext(AppConfigContext);
  const [isConnected, setIsConnected] = React.useState(false);
  const [sseAdapter, setSseAdapter] = React.useState<SSEAdapter | null>(null);
  const [messageCount, setMessageCount] = React.useState(0);

  React.useEffect(() => {
    if (appConfig) {
      const sseModel = new SSEModel(appConfig);
      const adapter = new SSEAdapter(sseModel);

      setSseAdapter(adapter);

      return () => {
        adapter.close();
      };
    }
  }, [appConfig]);

  const subscribe = (
    eventType: string,
    callback: (data: SSEEvent) => void,
    topic: string = "assignments",
  ) => {
    if (sseAdapter) {
      sseAdapter.subscribe(eventType, callback, topic);
    }
  };

  const unsubscribe = (eventType: string, callback: (data: SSEEvent) => void) => {
    if (sseAdapter) {
      sseAdapter.unsubscribe(eventType, callback);
    }
  };

  React.useEffect(() => {
    if (sseAdapter) {
      const handleConnectionStatus = (event: SSEEvent) => {
        setIsConnected(true);
        setMessageCount((prev) => prev + 1);

        setTimeout(() => {
          setIsConnected(false);
        }, 1000);
      };

      sseAdapter.subscribe("all", handleConnectionStatus, "assignments");

      return () => {
        sseAdapter.unsubscribe("all", handleConnectionStatus);
      };
    }
  }, [sseAdapter, isConnected, messageCount]);

  return (
    <SSEContext.Provider value={{ subscribe, unsubscribe, isConnected }}>
      {children}
    </SSEContext.Provider>
  );
}
